import React, { useEffect, useRef } from "react";
import { MessageCircleOff } from "lucide-react";

const FONT = "'Space Grotesk', sans-serif";

const trapFocus = (e, panelRef) => {
  if (e.key !== "Tab") return;
  const focusable = panelRef.current?.querySelectorAll(
    'button, [tabindex]:not([tabindex="-1"])',
  );
  if (!focusable?.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (e.shiftKey && document.activeElement === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault();
    first.focus();
  }
};

const EndChatConfirmModal = ({
  onConfirm,
  onCancel,
  title = "End live chat?",
  description = "Are you sure you want to end the live chat? The customer will go back to the AI assistant. Your copilot session will stay open.",
}) => {
  const panelRef = useRef(null);
  const returnFocusRef = useRef(null);
  const headingId = "end-chat-modal-title";
  const descId = "end-chat-modal-desc";

  useEffect(() => {
    returnFocusRef.current = document.activeElement;
    const cancelBtn = panelRef.current?.querySelector('[data-end-chat-cancel]');
    cancelBtn?.focus();
    return () => returnFocusRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel?.();
        return;
      }
      trapFocus(e, panelRef);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onCancel]);

  return (
    <div
      className="fixed inset-0 z-[999] flex items-center justify-center"
      style={{ fontFamily: FONT }}
    >
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-[2px]"
        onClick={onCancel}
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        aria-describedby={descId}
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden motion-safe:animate-[fadeIn_150ms_ease-out]"
      >
        <div className="h-1 w-full bg-gradient-to-r from-cyan-400 to-cyan-500" />

        <div className="px-6 pt-5 pb-6">
          <div className="flex gap-3">
            <div className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0 mt-0.5">
              <MessageCircleOff size={17} className="text-red-500" strokeWidth={2} />
            </div>

            <div className="flex-1 min-w-0">
              <h2
                id={headingId}
                className="text-base font-semibold text-[#253439] leading-snug"
              >
                {title}
              </h2>

              <p
                id={descId}
                className="text-sm text-gray-500 leading-relaxed mt-1.5"
              >
                {description}
              </p>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 mt-5">
            <button
              type="button"
              data-end-chat-cancel
              onClick={onCancel}
              className="px-4 py-1.5 text-sm text-gray-500 hover:text-gray-700 border border-gray-200 hover:border-gray-300 rounded-lg transition"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              className="px-4 py-1.5 text-sm font-medium rounded-lg transition bg-red-600 hover:bg-red-700 text-white"
            >
              End chat
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EndChatConfirmModal;
